// src/modules/callSync/components/CallDispositionModal.jsx
// Collects the outcome after a call. Writes a LeadActivity via the backend, so
// the lead's stage, follow-up date and analytics all update as they always have.

import React, { useEffect } from 'react';
import { Modal, Form, Select, Input, DatePicker, Alert, Space, Tag, Grid, message } from 'antd';
import dayjs from 'dayjs';
import { setCallDisposition } from '../api';
import { DISPOSITION_OPTIONS, formatDuration, statusMeta, isMeasured } from '../constants';

const { useBreakpoint } = Grid;

// `awaitingResult` carries the rep's own credit rule. Passed in by the provider
// rather than read from the session hook, so this component does not import the
// context that renders it.
export default function CallDispositionModal({
  call, open, onClose, onSaved, leadStatuses = [], awaitingResult = null,
}) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [form] = Form.useForm();
  const [saving, setSaving] = React.useState(false);
  const disposition = Form.useWatch('disposition', form);

  const needsFollowUp = DISPOSITION_OPTIONS
    .find((o) => o.value === disposition)?.requiresFollowUp;

  useEffect(() => {
    if (!open || !call) return;
    // Pre-select the outcome the phone already told us, so the rep confirms
    // rather than retypes. A call that never connected is almost always one of
    // these two.
    const suggested = call.status === 'NO_ANSWER' ? 'NO_ANSWER'
      : call.durationSeconds > 0 ? undefined
        : undefined;
    form.setFieldsValue({ disposition: suggested, notes: undefined, followUpAt: undefined });
  }, [open, call, form]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      await setCallDisposition(call.callId, {
        disposition: v.disposition,
        notes: v.notes,
        interestLevel: v.interestLevel,
        followUpAt: v.followUpAt ? v.followUpAt.toISOString() : undefined,
      });
      message.success('Call outcome saved');
      onSaved?.();
      onClose?.();
    } catch (err) {
      if (err?.errorFields) return; // form validation already shown
      message.error(err?.response?.data?.message || 'Could not save the outcome');
    } finally {
      setSaving(false);
    }
  };

  if (!call) return null;
  const measured = isMeasured(call);
  // Under a result-saved rule this form is not paperwork — it is what makes the
  // call count. Said only where it is TRUE, so the promise is never hollow.
  const earnsCredit = awaitingResult?.creditMode
    && awaitingResult.creditMode !== 'DIALLED_ONLY';

  return (
    <Modal
      title="Call outcome"
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="Save outcome"
      okButtonProps={{ loading: saving }}
      destroyOnClose
      width={isMobile ? '100%' : 520}
      style={isMobile ? { top: 8, maxWidth: 'calc(100vw - 16px)', margin: '0 auto' } : undefined}
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Tag color={statusMeta(call.status).color}>{statusMeta(call.status).label}</Tag>
        {call.durationSeconds > 0 && (
          <Tag color={measured ? 'green' : 'orange'}>
            {formatDuration(call.durationSeconds)}{!measured ? ' · confirming' : ''}
          </Tag>
        )}
        <Tag>{call.callId}</Tag>
      </Space>

      {/* An unmeasured duration almost always means the phone has not sent its
          call-log reading YET — a locked, dozing handset can take a minute. It
          is not a failure, and calling it one trained reps to distrust a figure
          that corrects itself a moment later. The banner says what is actually
          happening, and the number above updates in place when it arrives. */}
      {!measured && call.durationSeconds > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Confirming the exact talk time"
          description={"This figure is timed from dialling, so it still includes ringing. "
            + "The phone reports the measured talk time from its call log shortly after the "
            + "call — you can save the outcome now, the duration corrects itself."}
        />
      )}

      {earnsCredit && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message="Saving this counts the call towards your daily target"
          description={awaitingResult.requireSameBusinessDaySave
            ? 'A call with no recorded outcome earns nothing, and the result must be saved before today closes.'
            : 'A call with no recorded outcome earns nothing.'}
        />
      )}

      <Form form={form} layout="vertical">
        <Form.Item
          name="disposition"
          label="Outcome"
          rules={[{ required: true, message: 'Select what happened on this call' }]}
        >
          <Select options={DISPOSITION_OPTIONS.map((o) => ({ value: o.value, label: o.label, disabled: o.disabled }))} placeholder="What happened?" />
        </Form.Item>

        {needsFollowUp && (
          <Form.Item
            name="followUpAt"
            label="Follow-up date & time"
            rules={[{ required: true, message: 'A follow-up needs a date, or it will be forgotten' }]}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              disabledDate={(d) => d && d < dayjs().startOf('day')}
            />
          </Form.Item>
        )}

        {!!leadStatuses.length && (
          <Form.Item name="interestLevel" label="Interest level">
            <Select
              allowClear
              placeholder="Hot / Warm / Cold"
              options={leadStatuses.map((s) => ({ value: s.code, label: s.name }))}
            />
          </Form.Item>
        )}

        <Form.Item name="notes" label="What the customer said">
          <Input.TextArea rows={3} placeholder="Notes from the call" maxLength={2000} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}
